import { DirectorShareholderType, RiskLevelType } from '@/pages/users/components/UserAccountForm';
import { PrimaryContactMethodType } from '@/pages/users/components/UserContact';
import { Dayjs } from '@/utils/dayjs-config';
import { normalizeEmail } from '@/utils/email';
import { RequestData } from '@ant-design/pro-table';
import { filterNullUndefinedEmptyParams, request } from '@/utils/request';
import { Application } from './card-application';
import { ClientDocumentStatus } from './client-documents';
import { UserGroupItem } from './fee-setting-management';
import { AccountType } from './users';
import { vanillaClient, type RouterOutputs } from '@/trpc/client';

export enum UserStatus {
  ACTIVE = 'ACTIVE',
  SUSPENDED = 'SUSPENDED',
  CLOSED = 'CLOSED'
}

export type ClosureBlockingInstructionRecord = {
  id: string;
  refId: string | null;
  intent: string | null;
  status: string;
  createdAt: string;
};

export type ClosureBlockingInstructions = {
  generalInstructions: ClosureBlockingInstructionRecord[];
  instructions: ClosureBlockingInstructionRecord[];
  /** @deprecated Sample size retained for compatibility; use sampledTotal. */
  total: number;
  sampledTotal?: number;
  sampleLimitPerType?: number;
};

export type ClosureReadiness = RouterOutputs['accountClosure']['readiness'];
export type ClosureReadinessCheckStatus = ClosureReadiness['checks']['nav']['status'];
export type AccountClosureCleanupPreview = RouterOutputs['accountClosure']['prepareBalanceCleanup'];
export type AccountClosureCleanupResult = RouterOutputs['accountClosure']['executeBalanceCleanup'];

/** True when the account cannot receive operator-driven wallet/instruction actions (suspended or closed). */
export function isInactiveUserAccountStatus(status?: UserStatus | string | null): boolean {
  if (status == null || status === '') {
    return false;
  }
  return status === UserStatus.SUSPENDED || status === UserStatus.CLOSED;
}

/** Tooltip copy for disabled primary actions when `isInactiveUserAccountStatus` applies. */
export const INACTIVE_ACCOUNT_OPERATOR_TOOLTIP = 'Not available for suspended or closed accounts' as const;

export type RestrictedClientAccess = {
  requiresRestrictedAccess: boolean;
  restrictedAccessGranted: boolean;
  restrictedAccessReason: string;
  restrictedGroupIds: string[];
  missingRestrictedGroupIds: string[];
  capabilities: {
    canViewSensitiveClientData: boolean;
    canManageRestrictedClient: boolean;
    canImpersonateRestrictedClient: boolean;
    canBypassRestrictedClientAccess: boolean;
  };
};

export type AppxClientMetadata = {
  version?: string;
  build?: string;
  platform?: 'ios' | 'android' | 'web';
  osVersion?: string;
  timezone?: string;
  reportedAt: string;
};

export function formatAppxPlatformLabel(platform?: AppxClientMetadata['platform']): string | undefined {
  if (platform === 'ios') return 'iOS';
  if (platform === 'android') return 'Android';
  if (platform === 'web') return 'Web';
  return undefined;
}

export function formatAppxVersionLabel(metadata?: AppxClientMetadata | null): string {
  if (!metadata) {
    return 'Not reported';
  }

  const version = metadata.version ? `AppX ${metadata.version}` : 'AppX';
  const build = metadata.build ? ` (${metadata.build})` : '';
  return `${version}${build}`;
}

export function formatAppxPlatformOsLabel(metadata?: AppxClientMetadata | null): string {
  if (!metadata) {
    return 'Not reported';
  }

  const platform = formatAppxPlatformLabel(metadata.platform);
  if (platform && metadata.osVersion) {
    return `${platform} ${metadata.osVersion}`;
  }
  return platform || metadata.osVersion || 'Not reported';
}

export function formatAppxSummary(metadata?: AppxClientMetadata | null): string {
  if (!metadata) {
    return 'AppX: Not reported';
  }

  const platformOs = formatAppxPlatformOsLabel(metadata);
  return platformOs === 'Not reported'
    ? formatAppxVersionLabel(metadata)
    : `${formatAppxVersionLabel(metadata)} · ${platformOs}`;
}

export function isRestrictedClientSafeShell(user?: Pick<User, 'restrictedClientAccess' | 'safeShell'> | null): boolean {
  return Boolean(
    user?.safeShell ||
      (user?.restrictedClientAccess?.requiresRestrictedAccess && !user.restrictedClientAccess.restrictedAccessGranted)
  );
}

export interface User {
  referralCode: string;
  id: string;
  accountNumber?: string;
  accountId: string;
  accountTier: { id: number; tier: string };
  accountTierLabel: string;
  email: string;
  emailVerified: true;
  kycDocumentsStatus: {
    addressProof: string | null;
    idFront: string | null;
    idBack: string | null;
    selfie: string | null;
    passport: string | null;
  };
  kycLevel: {
    auto_approve: string;
    created_at: Date;
    description: { 'en-US': string; 'zh-HK': string; 'zh-CN': string };
    id: number;
    kyc_level: { 'en-US': string; 'zh-HK': string; 'zh-CN': string };
    kyc_required_fields: object[];
    kyc_user_required_fields: object[];
    next_level_id: number;
    updated_at: Date;
  };
  name: string;
  firstName: string;
  lastName: string;
  contactPerson: string;
  contactEmail: string;
  contactCountryCode: string;
  contactMobile: string;
  contactAddress: string;
  companyName: string;
  countryCode: string;
  accountType: AccountType;
  phone: string;
  phoneVerified: boolean;
  signupCode: string;
  signupDate: Date;
  riskLevel: string;
  nationality: string;
  idNumber: string;
  idCountry: string;
  passportNumber: string;
  dateOfBirth: Date;
  gender: string;
  occupation: string;
  vendor: {
    id: number;
    name: string;
    website: string;
  };
  relationshipManager: {
    id: string;
    name: string;
    telegramUserId: string;
  };
  metadata: {
    _hkmsosId?: string;
    _welcomeEmailSentAt?: Date;
    securitiesAccount?: { enabled?: boolean };
    fundAccount?: { enabled?: boolean };
    bondAccount?: { enabled?: boolean };
    fundAndBondAccount?: { enabled?: boolean };
    exchangeSubAccount?: { enabled?: boolean };
    statementOptions?: {
      statementName?: string;
      statementAddress?: string;
    };
    useNewFeeSchedule?: boolean;
    cardAccountTypeSupported?: string[];
  };
  status: UserStatus;
  newPasswordRequired: boolean;
  trustName: string;
  settlorName: string;
  serviceType: string;
  trustFullName: string;
  trustAccountName: string;
  lastLoginTime: Date;
  latestAppxClientMetadata?: AppxClientMetadata;
  secureEmailNotification: boolean;
  tuRatings: {
    tuLabel: string;
    override: number;
  };
  spe: string;
  clientSummary: string;
  groupIds: Array<string>;
  userGroups: Array<UserGroupItem>;
  restrictedClientAccess?: RestrictedClientAccess;
  safeShell?: boolean;
  crsReporting: boolean;
  usRelated: boolean;
  watchlist: boolean;
  cardProgramOptOut?: boolean;
  marketingOptOut?: boolean;
  taxCountry: string;
  tinNumber: string;
  trustSetupDate: Date;
  directorShareholderInfos: DirectorShareholderInfo[];
  preferLanguage: string;
  parentAccountId: string;
  closureReason?: string;
  closureDate?: Date;
  residentialAddress?: string;
  placeOfBusiness?: string;
  countryOfIncorporation?: string;
  dateOfIncorporation?: Date | Dayjs;
  notionPageId?: string;
  f2fVerified?: boolean;
  f2fVerifiedAt?: Date;
  anniversaryDate?: Date;
  industry?: string;
  annualIncome?: string;
  employmentStatus?: string;
  sourceOfWealth?: string;
  solutionName?: string;
  solutionId?: string;
  lastOnlineAt?: string;
  localFirstName?: string;
  localLastName?: string;
  poaStatus?: string;
  passportExpiryDate?: Date;
  idDocumentType: string;
  documentIssueDate?: Date;
  localContactAddress?: string;
  localResidentialAddress?: string;
  isPi?: boolean;
  piDeclarationRequired?: boolean;
  piType?: string;
  piConsentTime?: string;
  piDeclarationUpdatedAt?: string;
  piHoldingStructure?: string;
  piInstitutionalRegulatoryBody?: string;
  piInstitutionalLicenseNumber?: string;
  piApprovalStatus?: string;
}

export interface PaymentKYCInstructionsListItem {
  submittedAt: string;
  id: string;
  email: string;
  pendingDocumentCount: number;
  relationshipManager?: {
    id: string;
    name: string;
  };
  _hkmsosId?: string;
  status: {
    [key: string]: ClientDocumentStatus;
  };
  user: {
    id: string;
    accountNumber: string;
    name: string;
    email: string;
    accountType: string;
    companyName: string;
  };
}

export interface UsersBeneficiariesListItem {
  submittedAt: string;
  id: string;
  email: string;
  relationshipManager?: {
    id: string;
    name: string;
  };
  accountType: string;
  companyName: string;
  _hkmsosId?: string;
  user: {
    id: string;
    accountNumber: string;
    name: string;
  };
  status: string;
  reviewStatus: string;
  relationship: string;
  name: string;
  solution: string;
}

export enum UserBeneficiariesV2Status {
  NEW = 'NEW',
  APPROVED = 'APPROVED',
  REJECTED = 'REJECTED',
  REMOVED = 'REMOVED'
}

export enum UserBeneficiariesV2ReviewStatus {
  PENDING = 'PENDING',
  COMPLETED = 'COMPLETED',
  MISSING_INFO = 'MISSING_INFO'
}

export enum RelationShip {
  SPOUSE = 'SPOUSE',
  SON = 'SON',
  DAUGHTER = 'DAUGHTER',
  PARENT = 'PARENT',
  GRANDPARENT = 'GRANDPARENT',
  SIBLING = 'SIBLING',
  GRANDSON = 'GRANDSON',
  GRANDDAUGHTER = 'GRANDDAUGHTER',
  FRIEND = 'FRIEND',
  PARTNER = 'PARTNER',
  RELATIVE = 'RELATIVE',
  CHARITY = 'CHARITY',
  DIRECTOR = 'DIRECTOR',
  SHAREHOLDER_PERSONAL = 'SHAREHOLDER_PERSONAL',
  SHAREHOLDER_CORPORATE = 'SHAREHOLDER_CORPORATE',
  CORPORATION = 'CORPORATION',
  MYSELF = 'MYSELF',
  MY_UNBORN_CHILDREN = 'MY_UNBORN_CHILDREN',
  MY_UNBORN_GRANDCHILDREN = 'MY_UNBORN_GRANDCHILDREN',
  OTHER = 'OTHER'
}

export interface UsersCraFormListItem {
  id: string;
  riskLevel: string;
  preparedBy: string;
  date: string;
  user: {
    id: string;
    accountNumber: string;
    name: string;
    email: string;
    accountType: string;
    companyName: string;
  };
  relationshipManager?: {
    id: string;
    name: string;
  };
}

export interface UserDigitalAddressListItem {
  createdAt: string;
  chainType: string;
  id: string;
  walletAddress: string;
  userId: string;
  remarks: string;
  remarkChannel: string;
}

export interface UsersRemarks {
  id: string;
  name: string;
  email: string;
  objectType: string;
  userRemarks: string;
  createdAt: Date;
}

export interface UserRemarkListItem {
  id: string;
  object: {
    type: string;
    id: string;
    action: string;
  };
  remark: string;
  remarkChannel: string;
  time: Date;
  operator: {
    name: string;
  };
}
export interface UserBindingListItem {
  id: string;
  status: string;
  email: string;
  userName: string;
  emailVerified: boolean;
  mobileVerified: boolean;
  newPasswordRequired: string;
  countryCode: number;
  mobile: string;
  tfaEnabled: boolean;
  gaTfaEnabled: boolean;
  defaultUserId: string;
  vendor: {
    id: number;
    name: string;
  };
  accountDetails: Array<{
    accountNumber: string;
    userId: string;
  }>;
  lastLoginTime: string | null;
  locked: boolean;
}

interface UserFeaturesDTO {
  kycLevelId: number;
  name: string;
  features: {
    feature: string;
    enabled: boolean;
  }[];
}

export interface GetLoginAccountsParams {
  status: string;
  id: string;
  email: string;
  fromDate: Date;
  toDate: Date;
  isLoginSelect: boolean;
  userId: string;
}

export interface UserLoginHistoryListItem {
  acceptLanguage: string;
  createdAt: string;
  expiry: string;
  id: string;
  ipAddress: string;
  isSuccess: boolean;
  method: string;
  updatedAt: string;
  userAgent: string;
  userId: string;
  loginEmail: string;
  clientMetadata?: AppxClientMetadata;
}

export interface DirectorShareholderInfo {
  readonly id: string;
  name: string;
  chineseName: string;
  dateOfBirth: Dayjs;
  nationality: string;
  passportOrIdentityNumber: string;
  IssueingCountryOrRegion: string;
  passportExpiryDate: Dayjs;
  gender?: string;
  email: string;
  address: string;
  sharePercentage: number;
  allocationRatio: number;
  workPhone: string;
  mobilePhone: string;
  taxResidency: string;
  tinNumber: string;
  countryRisk: RiskLevelType;
  remark: string;
  remarkChannel: string;
  accountType: DirectorShareholderType;
}

export interface UserContact {
  id: string;
  phone: string;
  phoneCountryCode: string;
  phoneVerified: boolean;
  email: string;
  emailVerified: boolean;
  telegramId: string;
  wechatId: string;
  whatsapp: string;
  lineId: string;
  primaryContactMethod: PrimaryContactMethodType;
  linkedinId: string;
  facebookId: string;
  instagramId: string;
  userHavingMultipleLogin: boolean;
  notionPageId: string;
}

export enum BeneficiaryStatus {
  ACTIVE = 'ACTIVE',
  PENDING = 'PENDING',
  INACTIVE = 'INACTIVE',
  NEW = 'NEW',
  REMOVED = 'REMOVED'
}

export interface UserBeneficiaryListItem {
  id: string;
  user_id?: User;
  name: string;
  surname: string;
  given_name: string;
  name_in_chinese: string;
  dob: Date;
  gender: string;
  restendal_address: string;
  tax_residency: string;
  nationality: string;
  identification: string;
  pep: boolean;
  pep_details: string;
  us_tax_payer: boolean;
  us_tax_payer_details: string;
  tin: string;
  tin_details: string;
  relationship: string;
  contact_number: string;
  occupation: string;
  company_detail: string;
  allocation: number;
  status: BeneficiaryStatus;
  removed_at: Date;
  created_at: Date;
  updated_at: Date;
}

export enum ClientFootprint {
  VERIFICATION = 'VERIFICATION',
  DEPOSIT = 'DEPOSIT',
  REFERENCE = 'REFERENCE',
  REFERCODE = 'REFERCODE',
  CARD_APPLCATION = 'CARD_APPLCATION',
  PENDING_APPROVAL = 'PENDING_APPROVAL',
  COMPLETED = 'COMPLETED',
  NONE = 'NONE'
}

export interface UserSolutionListItem {
  id: string;
  solutionId: string;
  userId: string;
  name: string;
  clientFootprint: ClientFootprint;
  status: string;
  startedAt: Date;
  updatedAt: Date;
  default?: boolean;
  solutionType?: string;
  solutionCode?: string;
  operatorName?: string;
  onboardingMode?: 'SUMSUB' | 'LEGACY';
  cardApplication?: Application;
  cardProduct?: {
    id: number;
    code: string;
    name: string;
    imageUrl?: string;
    productType: string;
    currency: string;
    cardType: string;
    cardImageDetails?: Record<string, unknown>;
    scheme?: string;
  };
}

type Features = {
  editable: boolean;
  enabled: boolean;
  feature: string;
  value?: unknown;
};

export interface QueryListFeatureResponse {
  features: Features[];
  kycLevelId: number;
}

export interface UserBeneficiaryResponse {
  trustName: string;
  settlorName: string;
  trustFullName: string;
  trustAccountName: string;
  userBeneficiaries: UserBeneficiaryListItem[];
}

export interface InvalidateFeatureFlagCacheResponse {
  userId: string;
}

export async function queryUserById(params: { id: string }) {
  const data = await request<User>(`/users/${params.id}`, { params });
  return {
    data
  };
}

export async function queryUserDigitalAddress(params: any): Promise<RequestData<UserDigitalAddressListItem>> {
  const data = await request<RequestData<UserDigitalAddressListItem>>(`/user-digital-addresses/${params.userId}`, { params });
  return data;
}

export async function queryCurrent() {
  return request<any>('/~/me');
}

export async function queryMyRoutes() {
  return request<any>('/~/routes');
}

export async function getKycDocuments(userId: string) {
  return request<any>(`/users/${userId}/kyc-documents`, {
    getResponse: true
  });
}

export async function approveKyc(data: { userId: string; remark: string; remarkChannel: string }) {
  return request<any>(`/users/${data.userId}/kyc/approve`, {
    method: 'post',
    data: {
      remark: data.remark,
      remarkChannel: data.remarkChannel
    }
  });
}

export async function rejectKyc(data: { userId: string; remark: string; remarkChannel: string }) {
  return request<any>(`/users/${data.userId}/kyc/reject`, {
    method: 'post',
    data: {
      remark: data.remark,
      remarkChannel: data.remarkChannel
    }
  });
}

export async function approveKycDocuments(data: { documents: string[]; userId: string; remark: string; remarkChannel: string }) {
  return request<any>(`/users/${data.userId}/kyc/approve-document`, {
    method: 'post',
    data: {
      documents: data.documents,
      remark: data.remark,
      remarkChannel: data.remarkChannel
    }
  });
}

export async function rejectKycDocuments(data: { documents: string[]; userId: string; remark: string; remarkChannel: string }) {
  return request<any>(`/users/${data.userId}/kyc/reject-document`, {
    method: 'post',
    data: {
      documents: data.documents,
      remark: data.remark,
      remarkChannel: data.remarkChannel
    }
  });
}

export async function queryPendingKYCInstructions(params: any): Promise<RequestData<PaymentKYCInstructionsListItem>> {
  return await request<RequestData<PaymentKYCInstructionsListItem>>('/users/kyc/pending', {
    params: params
  });
}

export async function queryUsersBeneficiaries(params: any): Promise<RequestData<UsersBeneficiariesListItem>> {
  return await request<RequestData<UsersBeneficiariesListItem>>('/users/beneficiaries/pending', {
    params: params
  });
}

export async function queryUserClosureBlockers(userId: string): Promise<ClosureBlockingInstructions> {
  return await request<ClosureBlockingInstructions>(`/users/${userId}/closure-blockers`);
}

export async function queryUserClosureReadiness(userId: string): Promise<ClosureReadiness> {
  return vanillaClient.accountClosure.readiness.query({ userId });
}

export async function prepareAccountClosureBalanceCleanup(userId: string): Promise<AccountClosureCleanupPreview> {
  return vanillaClient.accountClosure.prepareBalanceCleanup.mutate({ userId });
}

export async function executeAccountClosureBalanceCleanup(input: {
  userId: string;
  previewId: string;
  selectedNegativeRowIds: string[];
  acknowledged: true;
  remark: string;
  _yotpToken: string;
}) {
  return vanillaClient.accountClosure.executeBalanceCleanup.mutate(input);
}

export async function queryUsersCraForm(params: any): Promise<RequestData<UsersCraFormListItem>> {
  return await request<RequestData<UsersCraFormListItem>>('/users/cra-form/pending', {
    params: params
  });
}

export async function uploadKycDocuments(data: { documents: object[]; userId: string; remark: string; remarkChannel: string }) {
  return await request<any>(`/users/${data.userId}/kyc-documents`, {
    method: 'post',
    data: {
      documents: data.documents,
      remark: data.remark,
      remarkChannel: data.remarkChannel
    }
  });
}

export async function sendEmail(data: { userId: string; subject: string; content: string; _yotpToken?: string; loginId?: string }) {
  return request<any>(`/users/${data.userId}/send-email`, {
    method: 'post',
    data: {
      loginId: data.loginId,
      subject: data.subject,
      content: data.content,
      _yotpToken: data._yotpToken
    }
  });
}

export async function previewEmail(data: { userId: string; subject: string; content: string; loginId?: string }) {
  return request<{ renderedHtml: string }>(`/users/${data.userId}/send-email/preview`, {
    method: 'post',
    data: {
      loginId: data.loginId,
      subject: data.subject,
      content: data.content
    }
  });
}

export async function queryUserRemarks(params: any): Promise<RequestData<UserRemarkListItem>> {
  const data = await request<RequestData<UserRemarkListItem>>(`/remarks`, { params });
  return data;
}

export async function queryUserLoginHistory(userId: string): Promise<RequestData<UserLoginHistoryListItem>> {
  return await request<RequestData<UserLoginHistoryListItem>>(`/user/${userId}/get-history`);
}

export async function queryUserContactById(userId: string) {
  const data = await request<UserContact>(`/users/${userId}/contact-info`);
  return {
    data
  };
}

export async function queryUserBinding(params: any): Promise<RequestData<UserBindingListItem>> {
  return await request<RequestData<UserBindingListItem>>(`/logins/account-associated`, { params });
}

export type LoginItem = {
  id: string;
  email: string;
  userName: string;
  vendor: {
    id: number;
    name: string;
  };
};

export async function queryLoginsByEmail(params: {
  email: string;
  vendorId: number;
  pageSize?: number;
}): Promise<RequestData<LoginItem>> {
  return await request<RequestData<LoginItem>>(`/logins`, {
    params: {
      ...params,
      email: normalizeEmail(params.email)
    }
  });
}

export type DuplicateClientMatchReason =
  | { type: 'ID_NUMBER' }
  | { type: 'PASSPORT_NUMBER' }
  | { type: 'NAME_SIMILAR'; score: number };

export type DuplicateClientCandidate = {
  userId: string;
  displayName: string;
  idNumberMasked: string | null;
  passportMasked: string | null;
  accountType: AccountType;
  status: string;
  createdAt: string;
  matchReasons: DuplicateClientMatchReason[];
};

export async function checkDuplicateClients(params: {
  vendorId: number;
  accountType: AccountType;
  firstName?: string;
  lastName?: string;
  localFirstName?: string;
  localLastName?: string;
  companyName?: string;
  idNumber?: string;
  passportNumber?: string;
}): Promise<{ candidates: DuplicateClientCandidate[] }> {
  return request<{ candidates: DuplicateClientCandidate[] }>('/users/check-duplicates', {
    params: filterNullUndefinedEmptyParams(params as Record<string, unknown>)
  });
}

export async function updateUserInfo(data: {
  userId: string;
  vendorId: number;
  phone: string;
  email: string;
  telegramId: string;
  wechat: string;
  whatsapp: string;
  lineId: string;
  primaryContactMethod: PrimaryContactMethodType;
  remark: string;
  remarkChannel: string;
  _yotpToken: string;
}) {
  return request<any>(`/users/${data.userId}/contact-info`, {
    method: 'post',
    data: {
      userId: data.userId,
      vendorId: data.vendorId,
      phone: data.phone,
      email: normalizeEmail(data.email),
      telegramId: data.telegramId,
      wechat: data.wechat,
      whatsapp: data.whatsapp,
      lineId: data.lineId,
      PrimaryContactMethodType: data.primaryContactMethod,
      _yotpToken: data._yotpToken
    }
  });
}
export async function bindAccountToLogin(data: {
  loginId: string;
  userId: string;
  vendorId: number;
  _yotpToken: string;
  email: string;
  emailVerified: boolean;
  mobile: string;
  countryCode: string;
  mobileVerified: boolean;
  isNewLogin: boolean;
  password: string;
  userName?: string;
}) {
  return await request<any>(`/logins/bind-account-to-login`, {
    method: 'post',
    data: {
      _yotpToken: data._yotpToken,
      loginId: data.loginId,
      userId: data.userId,
      vendorId: data.vendorId,
      email: normalizeEmail(data.email),
      emailVerified: data.emailVerified,
      mobile: data.mobile,
      countryCode: data.countryCode,
      mobileVerified: data.mobileVerified,
      isNewLogin: data.isNewLogin,
      password: data.password,
      userName: data.userName
    }
  });
}

export async function getUserNav(params: { userId: string; asset?: string }): Promise<number> {
  return request<number>(`/users/${params.userId}/nav`, {
    params: params.asset ? { asset: params.asset } : undefined
  });
}

export async function deleteLoginBinding(data: { loginId: string; userId: string; remarks: any; remarkChannel: any; _yotpToken: any }) {
  return await request<any>(`/logins/delete-login-binding`, {
    method: 'delete',
    data: {
      _yotpToken: data._yotpToken,
      loginId: data.loginId,
      userId: data.userId,
      remarks: data.remarks,
      remarkChannel: data.remarkChannel
    }
  });
}

export async function setAccountDefault(data: { loginId: string; recordId: string }) {
  return await request(`/logins/${data.loginId}/set-default-account/${data.recordId}`, {
    method: 'post',
    getResponse: true
  });
}

export async function registerNewLogin(data: {
  vendorId: number;
  _yotpToken: string;
  email?: string;
  emailVerified?: boolean;
  mobile?: string;
  countryCode?: string;
  mobileVerified?: boolean;
  password?: string;
}) {
  return await request<any>(`/logins`, {
    method: 'post',
    data: {
      vendorId: data.vendorId,
      email: normalizeEmail(data?.email),
      emailVerified: data?.emailVerified,
      mobile: data?.mobile,
      mobileVerified: data?.mobileVerified,
      password: data?.password,
      countryCode: data?.countryCode,
      _yotpToken: data._yotpToken
    }
  });
}

export async function queryListFeature(userId: string) {
  return request<QueryListFeatureResponse>(`/user-features/`, {
    method: 'GET',
    getResponse: true,
    params: { userId }
  });
}
export async function updateFeatures(data: { userId: string; features: any; _yotpToken: any }) {
  return await request<any>(`/user-features`, {
    method: 'Post',
    data: {
      userId: data.userId,
      features: data.features,
      _yotpToken: data._yotpToken
    }
  });
}

export async function invalidateFeatureFlagCache(data: { userId: string }): Promise<InvalidateFeatureFlagCacheResponse> {
  return await request<InvalidateFeatureFlagCacheResponse>('/user-features/invalidate-cache', {
    method: 'POST',
    data: {
      userId: data.userId
    }
  });
}

export async function queryUserSolution(params: any): Promise<RequestData<UserSolutionListItem>> {
  return await request<RequestData<UserSolutionListItem>>(`/solutions`, { params });
}

/**
 * Update director and shareholder information for a corporate user
 * @param userId - User ID
 * @param directorShareholderInfos - Array of director/shareholder information
 * @param remark - Mandatory remark explaining the reason for update
 * @param remarkChannel - Channel for the remark (optional, defaults to OPERATIONS)
 * @param _yotpToken - MFA token
 */
export async function updateDirectorShareholderInfos(data: {
  userId: string;
  directorShareholderInfos: any[];
  remark: string;
  remarkChannel?: string;
  _yotpToken: string;
}) {
  return await request<{ id: string; success: boolean }>(`/users/${data.userId}/director-shareholder-infos`, {
    method: 'PUT',
    data: {
      directorShareholderInfos: data.directorShareholderInfos,
      remark: data.remark,
      remarkChannel: data.remarkChannel,
      _yotpToken: data._yotpToken
    }
  });
}

export async function updateUserRelationshipManager(
  userId: string,
  data: { relationshipManagerId: string | null; remarks: string; remarkChannel: string; _yotpToken: string }
) {
  return request<{ success: boolean }>(`/users/${userId}/update-relationship-manager`, {
    method: 'POST',
    data: data
  });
}
